import { org_sector_type_enum } from '@predium/enums';
import { maybeEnum } from '@predium/utils';
import { inferParserType } from 'nuqs';
import { SEARCH_PARAMS } from './search-params';
import { serializeQuery } from './search_params/utils';
import { buildPath, URLParam } from './utils';

const ROOT = '/';

type IDParam = { id: URLParam };
type SearchParams<TSearchParams> = Partial<inferParserType<TSearchParams>>;

export const PATHS = {
  root: () => ROOT,
  notFound: () => buildPath(PATHS.root(), '404'),
  statusQuoAnalysis: {
    root: () => buildPath(PATHS.root(), 'analysis'),
    buildings: (query?: SearchParams<typeof SEARCH_PARAMS.statusQuoAnalysis.buildings>) =>
      buildPath(
        PATHS.statusQuoAnalysis.root(),
        'buildings',
        serializeQuery(SEARCH_PARAMS.statusQuoAnalysis.buildings, query),
      ),
    noBuildings: () => buildPath(PATHS.statusQuoAnalysis.buildings(), 'no-buildings'),
    building: (params: IDParam, query?: SearchParams<typeof SEARCH_PARAMS.statusQuoAnalysis.building>) =>
      buildPath(
        PATHS.statusQuoAnalysis.buildings(),
        params.id,
        serializeQuery(SEARCH_PARAMS.statusQuoAnalysis.building, query),
      ),
    portfolio: (query?: SearchParams<typeof SEARCH_PARAMS.statusQuoAnalysis.portfolio>) =>
      buildPath(
        PATHS.statusQuoAnalysis.root(),
        'portfolio',
        serializeQuery(SEARCH_PARAMS.statusQuoAnalysis.portfolio, query),
      ),
    economicUnits: (query?: SearchParams<typeof SEARCH_PARAMS.statusQuoAnalysis.economicUnits>) => {
      const sector = maybeEnum(org_sector_type_enum, sessionStorage.getItem('org-sector'));

      return buildPath(
        PATHS.statusQuoAnalysis.root(),
        sector === org_sector_type_enum.BANKING ? 'transaction' : 'economic-unit',
        serializeQuery(SEARCH_PARAMS.statusQuoAnalysis.economicUnits, query),
      );
    },
  },
  actionPlanning: {
    root: () => buildPath(PATHS.root(), 'action-planning'),
    scenarios: () => buildPath(PATHS.actionPlanning.root(), 'scenarios'),
    scenario: (params: { scenarioId: URLParam }, query?: SearchParams<typeof SEARCH_PARAMS.actionPlanning.scenario>) =>
      buildPath(
        PATHS.actionPlanning.scenarios(),
        params.scenarioId,
        serializeQuery(SEARCH_PARAMS.actionPlanning.scenario, query),
      ),
    actionPlan: (params: { stableId: URLParam; scenarioId: URLParam }) =>
      buildPath(PATHS.actionPlanning.scenario({ scenarioId: params.scenarioId }), 'action-plans', params.stableId),
  },
  reporting: {
    root: () => buildPath(PATHS.root(), 'reporting'),
    reports: (query?: SearchParams<typeof SEARCH_PARAMS.reports>) =>
      buildPath(PATHS.reporting.root(), 'reports', serializeQuery(SEARCH_PARAMS.reports, query)),
    report: (params: IDParam, query?: SearchParams<typeof SEARCH_PARAMS.report>) =>
      buildPath(PATHS.reporting.reports(), params.id, serializeQuery(SEARCH_PARAMS.report, query)),
  },
  drive: {
    root: () => buildPath(PATHS.root(), 'drive'),
    folder: (params: IDParam) => buildPath(PATHS.drive.root(), 'folder', params.id),
  },
  dataCollection: {
    root: () => buildPath(PATHS.root(), 'data-collection'),
    buildings: (_ = null, query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.buildings>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'buildings',
        serializeQuery(SEARCH_PARAMS.dataCollection.buildings, query),
      ),
    building: (params: IDParam, query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.building>) =>
      buildPath(
        PATHS.dataCollection.buildings(),
        params.id,
        serializeQuery(SEARCH_PARAMS.dataCollection.building, query),
      ),
    buildingDraftNew: () => buildPath(PATHS.dataCollection.buildings(), 'building-drafts', 'new'),
    buildingDraft: (params: IDParam) => buildPath(PATHS.dataCollection.buildings(), 'building-drafts', params.id),
    buildingConsumptionInvoice: (params: IDParam & { consumptionInvoiceId: URLParam }) =>
      buildPath(PATHS.dataCollection.building({ id: params.id }), 'consumption-invoice', params.consumptionInvoiceId),
    buildingConsumptionInvoiceDraft: (params: IDParam & { draftId: URLParam }) =>
      buildPath(PATHS.dataCollection.building({ id: params.id }), 'consumption-invoice', 'drafts', params.draftId),
    energySupplierCreate: () => buildPath(PATHS.dataCollection.root(), 'create-energy-supplier'),
    energySuppliers: (query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.energySuppliers>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'energy-suppliers',
        serializeQuery(SEARCH_PARAMS.dataCollection.energySuppliers, query),
      ),
    energySupplier: (params: IDParam) => buildPath(PATHS.dataCollection.energySuppliers(), params.id),
    energySupplierDrafts: () => buildPath(PATHS.dataCollection.energySuppliers(), 'drafts'),
    energySupplierDraft: (params: IDParam) => buildPath(PATHS.dataCollection.energySupplierDrafts(), params.id),
    consumptionHub: (query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.consumptionHub>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'consumption-hub',
        serializeQuery(SEARCH_PARAMS.dataCollection.consumptionHub, query),
      ),
    consumptionInvoices: (query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.consumptionInvoices>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'consumption-invoices',
        serializeQuery(SEARCH_PARAMS.dataCollection.consumptionInvoices, query),
      ),
    consumptionInvoiceItem: (params: IDParam) => buildPath(PATHS.dataCollection.consumptionInvoices(), params.id),
    consumptionDataHub: (query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.consumptionDataHub>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'consumption-data-hub',
        serializeQuery(SEARCH_PARAMS.dataCollection.consumptionDataHub, query),
      ),
    economicUnits: (query?: SearchParams<typeof SEARCH_PARAMS.dataCollection.economicUnits>) =>
      buildPath(
        PATHS.dataCollection.root(),
        'economic-units',
        serializeQuery(SEARCH_PARAMS.dataCollection.economicUnits, query),
      ),
    portfolios: () => buildPath(PATHS.dataCollection.root(), 'portfolios'),
  },
  org: {
    root: () => buildPath(PATHS.root(), 'org'),
    users: () => buildPath(PATHS.org.root(), 'users'),
    user: (params: IDParam) => buildPath(PATHS.org.users(), params.id),
    settings: () => buildPath(PATHS.org.root(), 'settings'),
    standardEnergyPrices: () => buildPath(PATHS.org.settings(), 'standard-energy-prices'),
  },
  // Connectors live under the Org Settings hierarchy so the breadcrumb auto-derives
  // as "Administrator > Organisation verwalten > Konnektor". The SmartMeterIntegrations
  // card on /org/settings is the canonical entry point for the connectors list.
  connectors: {
    root: () => buildPath(PATHS.org.settings(), 'connectors'),
    list: () => buildPath(PATHS.connectors.root(), 'list'),
    detail: (id: string) => buildPath(PATHS.connectors.root(), id),
    meter: (connectorId: string, meterId: string) => buildPath(PATHS.connectors.detail(connectorId), 'meters', meterId),
  },
};
